import React from 'react';
import { useCSVDownloader } from 'react-papaparse';
import {
  FiDatabase,
  FiSearch,
  FiRadio,
  FiChevronDown,
  FiChevronUp,
  FiAlignLeft,
  FiUploadCloud,
  FiDownload,
  FiTrash2,
  FiSave,
  FiCheckCircle,
} from 'react-icons/fi';
import { useGridApiRef, gridFilteredRowCountSelector } from '@mui/x-data-grid';
import DataGrid from '../../UI/DataGrid/DataGrid';
import S from './SensorDataTab.module.scss';
import { useSensorDataTab } from './useSensorDataTab';
import SensorHistoryPopup from './SensorHistoryPopup';
import SensorImportTab from './SensorImportTab';
import SensorChartTab from './SensorChartTab';

const DIACRITICS_RE = new RegExp('[\\u0300-\\u036f]', 'g');

const stripAccents = (str) =>
  String(str ?? '')
    .normalize('NFD')
    .replace(DIACRITICS_RE, '');

// Positions come from components metadata; avoid parsing free-text sensor fields.
const parsePositions = (capteur) => {
  const components = Array.isArray(capteur?.component_list)
    ? capteur.component_list
    : [];

  const byPosition = new Map();

  components.forEach((component) => {
    const position = Number(
      component?.position_in_sensor ?? component?.position
    );
    if (!Number.isFinite(position) || position <= 0) return;

    if (!byPosition.has(position)) {
      byPosition.set(position, {
        position,
        prefix: String(component?.prefix ?? '').trim(),
      });
    }
  });

  return Array.from(byPosition.values()).sort(
    (a, b) => a.position - b.position
  );
};

const SensorDataTab = () => {
  const { CSVDownloader } = useCSVDownloader();
  const [activeTab, setActiveTab] = React.useState('data');
  const [sensorSearch, setSensorSearch] = React.useState('');
  const [showChart, setShowChart] = React.useState(false);
  const [chartSensorKey, setChartSensorKey] = React.useState('');
  const [sensorFilter, setSensorFilter] = React.useState('tous');
  const [sortBy, setSortBy] = React.useState('name-asc');
  const [sortOpen, setSortOpen] = React.useState(false);
  const [expandedSensors, setExpandedSensors] = React.useState({});
  const gridApiRef = useGridApiRef();
  const [filteredRowCount, setFilteredRowCount] = React.useState(0);
  const [hasGridFilter, setHasGridFilter] = React.useState(false);

  const {
    capteurs,
    loadingCapteurs,
    sensorPositionMap,
    toggleSensor,
    togglePosition,
    hasSelection,
    startDateTime,
    setStartDateTime,
    endDateTime,
    setEndDateTime,
    nValues,
    setNValues,
    rows,
    rowSelectionModel,
    handleRowSelectionModelChange,
    paginationModel,
    setPaginationModel,
    loadingData,
    saving,
    error,
    fetched,
    columns,
    dirtyCount,
    historyMap,
    fetchData,
    handleBulkDelete,
    handleSaveAll,
    handleProcessRowUpdate,
    handleProcessRowUpdateError,
    historyPopupData,
    handleCloseHistory,
    getSensorNameById,
  } = useSensorDataTab();

  const refreshFilteredCounter = React.useCallback(
    (model) => {
      const hasItems = (model?.items ?? []).some(
        (item) =>
          item?.field &&
          item?.operator &&
          item?.value !== undefined &&
          item?.value !== null &&
          item?.value !== ''
      );
      const hasQuick =
        (model?.quickFilterValues ?? []).join('').trim().length > 0;
      const isFiltered = hasItems || hasQuick;
      setHasGridFilter(isFiltered);

      if (!isFiltered) {
        setFilteredRowCount(rows.length);
        return;
      }

      // Wait for DataGrid internal state to settle before reading selector.
      requestAnimationFrame(() => {
        const nextCount = gridFilteredRowCountSelector(gridApiRef);
        setFilteredRowCount(
          Number.isFinite(nextCount) ? nextCount : rows.length
        );
      });
    },
    [gridApiRef, rows.length]
  );

  const sensorKeys = React.useMemo(() => {
    const seen = new Set();
    const keys = [];
    rows.forEach((r) => {
      const k = `${r._sensorId}_${r.position_in_sensor}`;
      if (!seen.has(k)) {
        seen.add(k);
        keys.push({
          key: k,
          label: `${getSensorNameById(Number(r._sensorId))} — Pos. ${r.position_in_sensor}`,
        });
      }
    });
    return keys;
  }, [rows, getSensorNameById]);

  React.useEffect(() => {
    if (sensorKeys.length > 0) setChartSensorKey(sensorKeys[0].key);
  }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!hasGridFilter) {
      setFilteredRowCount(rows.length);
    }
  }, [hasGridFilter, rows.length]);

  // On fetch completion, reveal the table and auto-show the first selected chart.
  React.useEffect(() => {
    if (fetched && rows.length > 0) setShowChart(true);
  }, [fetched, rows.length]);

  const chartRows = React.useMemo(
    () =>
      chartSensorKey
        ? rows.filter(
            (r) => `${r._sensorId}_${r.position_in_sensor}` === chartSensorKey
          )
        : rows,
    [rows, chartSensorKey]
  );

  // Préfixe d'une position (issu du component_list du capteur), utilisé comme
  // libellé de la valeur dans l'infobulle du graphique.
  const getPositionPrefix = React.useCallback(
    (sensorId, position) => {
      const capteur = capteurs.find(
        (c) => Number(c?.sensor_id ?? c?.id) === Number(sensorId)
      );
      if (!capteur) return '';
      const match = parsePositions(capteur).find(
        (p) => Number(p.position) === Number(position)
      );
      return match?.prefix ?? '';
    },
    [capteurs]
  );

  const selectedSensorCount = React.useMemo(
    () =>
      Object.values(sensorPositionMap).filter(
        (positions) => positions.length > 0
      ).length,
    [sensorPositionMap]
  );

  const toggleExpand = React.useCallback((sId) => {
    setExpandedSensors((prev) => ({ ...prev, [sId]: !prev[sId] }));
  }, []);

  const visibleCapteurs = React.useMemo(() => {
    const q = stripAccents(sensorSearch.trim().toLowerCase());
    const filtered = capteurs.filter((c) => {
      const sId = String(c.sensor_id ?? c.id ?? '');
      const sName = stripAccents(
        String(c.sensor_name ?? c.name ?? '').toLowerCase()
      );
      const matchesSearch =
        !q || sId.toLowerCase().includes(q) || sName.includes(q);
      if (!matchesSearch) return false;

      if (sensorFilter === 'selectionnes') {
        return (sensorPositionMap[sId]?.length ?? 0) > 0;
      }
      return true;
    });

    const direction = sortBy.endsWith('asc') ? 1 : -1;
    const byName = sortBy.startsWith('name');
    return [...filtered].sort((a, b) => {
      if (byName) {
        const an = String(a.sensor_name ?? a.name ?? '');
        const bn = String(b.sensor_name ?? b.name ?? '');
        return an.localeCompare(bn) * direction;
      }
      return (
        (Number(a.sensor_id ?? a.id) - Number(b.sensor_id ?? b.id)) * direction
      );
    });
  }, [capteurs, sensorSearch, sensorFilter, sortBy, sensorPositionMap]);

  if (loadingCapteurs) {
    return (
      <div className={S.loadingState}>
        <div className={S.loadingCard}>
          <div className={S.loadingSpinner} />
          <p>Chargement de la liste des capteurs...</p>
        </div>
      </div>
    );
  }

  const tabBar = (
    <div className={S.tabBar}>
      <button
        type="button"
        className={[
          S.tabButton,
          activeTab === 'data' ? S.tabButtonActive : '',
        ].join(' ')}
        onClick={() => setActiveTab('data')}
      >
        <FiDatabase size={15} />
        Données
      </button>
      <button
        type="button"
        className={[
          S.tabButton,
          activeTab === 'import' ? S.tabButtonActive : '',
        ].join(' ')}
        onClick={() => setActiveTab('import')}
      >
        <FiUploadCloud size={15} />
        Import CSV
      </button>
    </div>
  );

  return (
    <div className={S.sensorDataTab}>
      <div className={`${S.content} ${!fetched ? S.contentInitial : ''}`}>
        {activeTab === 'import' ? (
          <div className={S.importScroll}>
            {tabBar}
            <SensorImportTab />
          </div>
        ) : (
          <>
            {/* ── Selector bar + chart side by side ── */}
            <div
              className={`${S.topPanel} ${!fetched ? S.topPanelInitial : ''}`}
            >
              {/* LEFT: filter controls */}
              <div
                className={`${S.sensorSelectorBar} ${!fetched ? S.sensorSelectorBarFull : ''}`}
              >
                {tabBar}

                {!fetched && (
                  <div className={S.selectorIntro}>
                    <span className={S.selectorIntroIcon}>
                      <FiDatabase size={22} />
                    </span>
                    <h3 className={S.selectorIntroTitle}>
                      Sélection des données
                    </h3>
                    <p className={S.selectorIntroSubtitle}>
                      Choisissez les capteurs, positions et la période, puis
                      chargez les données.
                    </p>
                  </div>
                )}

                <div className={S.selectorGroup}>
                  <div className={S.selectorLabelRow}>
                    <span className={S.selectorLabel}>
                      Capteurs et positions
                    </span>
                    {selectedSensorCount > 0 && (
                      <span className={S.selectedCountBadge}>
                        {selectedSensorCount} capteur
                        {selectedSensorCount > 1 ? 's' : ''} sélectionné
                        {selectedSensorCount > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>

                  <div className={S.searchInputWrap}>
                    <FiSearch className={S.searchIcon} size={15} />
                    <input
                      type="text"
                      className={S.sensorSearchInput}
                      value={sensorSearch}
                      onChange={(e) => setSensorSearch(e.target.value)}
                      placeholder="Rechercher par nom ou ID"
                    />
                  </div>

                  <div className={S.filterRow}>
                    <div className={S.filterTabs}>
                      {[
                        { id: 'tous', label: 'Tous' },
                        { id: 'selectionnes', label: 'Sélectionnés' },
                      ].map((tab) => (
                        <button
                          key={tab.id}
                          type="button"
                          className={`${S.filterTab} ${
                            sensorFilter === tab.id ? S.filterTabActive : ''
                          }`}
                          onClick={() => setSensorFilter(tab.id)}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>

                    <div className={S.sortWrap}>
                      <button
                        type="button"
                        className={S.sortButton}
                        onClick={() => setSortOpen((v) => !v)}
                      >
                        <FiAlignLeft size={14} />
                        Trier
                        <FiChevronDown size={13} />
                      </button>
                      {sortOpen && (
                        <div className={S.sortMenu}>
                          {[
                            { id: 'name-asc', label: 'Nom (A → Z)' },
                            { id: 'name-desc', label: 'Nom (Z → A)' },
                            { id: 'id-asc', label: 'ID croissant' },
                            { id: 'id-desc', label: 'ID décroissant' },
                          ].map((opt) => (
                            <button
                              key={opt.id}
                              type="button"
                              className={`${S.sortMenuItem} ${
                                sortBy === opt.id ? S.sortMenuItemActive : ''
                              }`}
                              onClick={() => {
                                setSortBy(opt.id);
                                setSortOpen(false);
                              }}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={S.capteurCheckList}>
                    {!capteurs.length && (
                      <span className={S.noSensor}>
                        Aucun capteur disponible
                      </span>
                    )}
                    {!!capteurs.length && !visibleCapteurs.length && (
                      <span className={S.noSensor}>Aucun capteur trouvé</span>
                    )}
                    {visibleCapteurs.map((c) => {
                      const sId = String(c.sensor_id ?? c.id ?? '');
                      const isChecked = !!sensorPositionMap[sId];
                      const selectedPositions = sensorPositionMap[sId] ?? [];
                      const sensorName =
                        c.sensor_name ?? c.name ?? `Capteur ${sId}`;
                      const positions = parsePositions(c);
                      const isExpanded = !!expandedSensors[sId] || isChecked;
                      return (
                        <div
                          key={sId}
                          className={`${S.capteurCheckItem} ${
                            isChecked ? S.capteurCheckItemActive : ''
                          }`}
                        >
                          <div className={S.capteurRow}>
                            <input
                              type="checkbox"
                              className={S.capteurCheckbox}
                              checked={isChecked}
                              onChange={() => toggleSensor(sId)}
                            />
                            <span className={S.capteurTypeIcon}>
                              <FiRadio size={16} />
                            </span>
                            <span className={S.capteurInfo}>
                              <span className={S.capteurName}>
                                {sensorName}
                              </span>
                              <span className={S.capteurId}>ID: {sId}</span>
                            </span>
                            <span className={S.capteurTypeBadge}>Capteur</span>
                            {positions.length > 0 && (
                              <button
                                type="button"
                                className={S.positionsToggle}
                                onClick={() => toggleExpand(sId)}
                              >
                                {positions.length} position
                                {positions.length > 1 ? 's' : ''}
                                {isExpanded ? (
                                  <FiChevronUp size={14} />
                                ) : (
                                  <FiChevronDown size={14} />
                                )}
                              </button>
                            )}
                          </div>
                          {isExpanded && positions.length > 0 && (
                            <div className={S.positionCheckList}>
                              <span className={S.positionsLabel}>
                                Positions
                              </span>
                              <div className={S.positionChips}>
                                {positions.map((componentPosition) => (
                                  <label
                                    key={componentPosition.position}
                                    className={`${S.positionChip} ${
                                      selectedPositions.includes(
                                        componentPosition.position
                                      )
                                        ? S.positionChipActive
                                        : ''
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedPositions.includes(
                                        componentPosition.position
                                      )}
                                      onChange={() =>
                                        togglePosition(
                                          sId,
                                          componentPosition.position
                                        )
                                      }
                                    />
                                    <span
                                      title={
                                        (componentPosition.prefix || 'Pos.') +
                                        ` (${componentPosition.position})`
                                      }
                                    >
                                      {(componentPosition.prefix || 'Pos.') +
                                        ` (${componentPosition.position})`}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className={S.fieldsRow}>
                  <div className={S.selectorGroup}>
                    <label className={S.selectorLabel} htmlFor="sd-date-start">
                      Date début
                    </label>
                    <div className={S.inputWithClear}>
                      <input
                        id="sd-date-start"
                        type="datetime-local"
                        className={S.dateInput}
                        value={startDateTime}
                        onChange={(e) => setStartDateTime(e.target.value)}
                      />
                      {startDateTime && (
                        <button
                          type="button"
                          className={S.clearInputButton}
                          title="Vider la date de début"
                          onClick={() => setStartDateTime('')}
                        >
                          X
                        </button>
                      )}
                    </div>
                  </div>

                  <div className={S.selectorGroup}>
                    <label className={S.selectorLabel} htmlFor="sd-date-end">
                      Date fin
                    </label>
                    <div className={S.inputWithClear}>
                      <input
                        id="sd-date-end"
                        type="datetime-local"
                        className={S.dateInput}
                        value={endDateTime}
                        onChange={(e) => setEndDateTime(e.target.value)}
                      />
                      {endDateTime && (
                        <button
                          type="button"
                          className={S.clearInputButton}
                          title="Vider la date de fin"
                          onClick={() => setEndDateTime('')}
                        >
                          X
                        </button>
                      )}
                    </div>
                  </div>

                  <div className={S.selectorGroup}>
                    <label className={S.selectorLabel} htmlFor="sd-n-values">
                      N derniers points
                    </label>
                    <div className={S.inputWithClear}>
                      <input
                        id="sd-n-values"
                        type="number"
                        min={1}
                        step={1}
                        inputMode="numeric"
                        className={S.nValuesInput}
                        value={nValues}
                        placeholder="Ex. : 1000"
                        onChange={(e) => {
                          const next = e.target.value;
                          if (next === '' || /^[1-9]\d*$/.test(next))
                            setNValues(next);
                        }}
                      />
                      {nValues && (
                        <button
                          type="button"
                          className={S.clearInputButton}
                          title="Vider N derniers points"
                          onClick={() => setNValues('')}
                        >
                          X
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <button
                  className={S.btnFetch}
                  onClick={fetchData}
                  disabled={!hasSelection || loadingData}
                >
                  <FiUploadCloud size={17} />
                  {loadingData ? 'Chargement…' : 'Charger les données'}
                </button>
              </div>

              {/* RIGHT COLUMN: chart (sticky) + toolbar + table */}
              {fetched && (
                <div className={S.rightColumn}>
                  {rows.length > 0 && (
                    <div
                      className={`${S.selectorChartArea} ${
                        showChart ? S.chartTall : ''
                      }`}
                    >
                      <SensorChartTab
                        rows={chartRows}
                        historyMap={historyMap}
                        getSensorNameById={getSensorNameById}
                        getPositionPrefix={getPositionPrefix}
                        showChart={showChart}
                        onToggleChart={() => setShowChart((v) => !v)}
                        sensorKeys={sensorKeys}
                        chartSensorKey={chartSensorKey}
                        onChartSensorKeyChange={setChartSensorKey}
                      />
                    </div>
                  )}

                  {error && <div className={S.alertErreur}>{error}</div>}

                  {rows.length > 0 && (
                    <div className={S.sensorGridWrapper}>
                      <div className={S.gridToolbar}>
                        <div className={S.gridToolbarLeft}>
                          <span className={S.rowCountPill}>
                            {hasGridFilter ? filteredRowCount : rows.length}
                            {' / '}
                            {rows.length} ligne(s)
                          </span>
                          <CSVDownloader
                            data={rows.map(
                              ({
                                id,
                                _dirty,
                                _sensorId,
                                old_time_unix,
                                old_value,
                                old_calc_value,
                                ...rest
                              }) => ({ sensor_id: _sensorId, ...rest })
                            )}
                            filename={`export_capteur_${new Date().toISOString().slice(0, 10)}`}
                            bom
                            config={{ delimiter: ';' }}
                            className={S.btnExport}
                          >
                            <FiDownload size={15} />
                            Exporter CSV
                          </CSVDownloader>
                        </div>
                        <div className={S.gridToolbarRight}>
                          <button
                            className={S.btnBulkDelete}
                            onClick={handleBulkDelete}
                            disabled={
                              !rowSelectionModel.length || saving || loadingData
                            }
                          >
                            <FiTrash2 size={14} />
                            Supprimer la sélection ({rowSelectionModel.length})
                          </button>
                          {dirtyCount > 0 ? (
                            <span className={S.badgeModifie}>
                              {dirtyCount} ligne(s) non sauvegardée(s)
                            </span>
                          ) : (
                            <span className={S.badgeSynchro}>
                              <FiCheckCircle size={14} />
                              Toutes les modifications sont sauvegardées
                            </span>
                          )}
                          <button
                            className={S.btnSaveData}
                            onClick={handleSaveAll}
                            disabled={saving || dirtyCount === 0}
                          >
                            <FiSave size={14} />
                            {saving
                              ? 'Enregistrement…'
                              : `Enregistrer (${dirtyCount})`}
                          </button>
                        </div>
                      </div>

                      <div className={S.gridBody}>
                        <DataGrid
                          apiRef={gridApiRef}
                          className={S.sensorDataGrid}
                          rows={rows}
                          columns={columns}
                          loading={loadingData}
                          checkboxSelection
                          checkboxSelectionVisibleOnly
                          rowSelectionModel={rowSelectionModel}
                          onRowSelectionModelChange={
                            handleRowSelectionModelChange
                          }
                          onFilterModelChange={refreshFilteredCounter}
                          onStateChange={() => {
                            if (!hasGridFilter) return;
                            const nextCount =
                              gridFilteredRowCountSelector(gridApiRef);
                            setFilteredRowCount(
                              Number.isFinite(nextCount)
                                ? nextCount
                                : rows.length
                            );
                          }}
                          paginationModel={paginationModel}
                          onPaginationModelChange={setPaginationModel}
                          editMode="row"
                          processRowUpdate={handleProcessRowUpdate}
                          onProcessRowUpdateError={handleProcessRowUpdateError}
                          pageSizeOptions={[
                            25,
                            50,
                            100,
                            { value: -1, label: 'All' },
                          ]}
                          initialState={{
                            pagination: { paginationModel: { pageSize: 25 } },
                            sorting: {
                              sortModel: [{ field: 'time_unix', sort: 'asc' }],
                            },
                          }}
                          disableRowSelectionOnClick
                          getRowClassName={(params) =>
                            params.row._dirty ? S.rowDirty : ''
                          }
                        />
                      </div>
                    </div>
                  )}

                  {rows.length === 0 && !loadingData && (
                    <p className={S.emptyState}>
                      Aucune donnée trouvée pour la sélection en cours.
                    </p>
                  )}
                </div>
              )}
            </div>

            {!fetched && error && <div className={S.alertErreur}>{error}</div>}
          </>
        )}
      </div>

      {historyPopupData && (
        <SensorHistoryPopup
          row={historyPopupData.row}
          entries={historyPopupData.entries}
          sensorName={getSensorNameById(historyPopupData.row?._sensorId)}
          onClose={handleCloseHistory}
        />
      )}
    </div>
  );
};

export default SensorDataTab;
